import _ from "lodash";
import PropTypes from "prop-types";
import React, { Component } from "react";
import ReactMarkdown from "react-markdown";
import { connect } from "react-redux";
import Link from "../../../components/LinkWithCountry/LinkWithCountry";
import * as userActions from "../../../redux/actions/user";
import * as userRepo from "../../../shared/repos/graphql/user";
import styles from "./CreateAccount.module.css";

// COMPONENT
import Button from "../../../components/Button/Button";

// FORMS
import { isRecaptcha } from "../../../shared/utilities/config";
import {
  handleFormInput,
  isValid
} from "../../../shared/utilities/validations";

// HELPERS
import CheckBox from "../../../components/CheckBox/CheckBox";
import SimpleInput from "../../../components/Input/SimpleInput";
import PhoneWidget from "../../../components/PhoneWidget/PhoneWidget";
import ResendOtp from "../../../components/ResendOtp/ResendOtp";
import { Tab, Tabs } from "../../../components/Tabs/Tabs";
import * as elementsActions from "../../../redux/actions/elements";
import captchaAction from "../../../shared/constants/captchaAction";
import cmsKeys from "../../../shared/constants/cms";
import { LABELS } from "../../../shared/constants/labels";
import subscription from "../../../shared/constants/subscription";
import * as cms from "../../../shared/repos/cms/cms";
import { setCaptchaToken } from "../../../shared/utilities/captcha";
import { getKeyValue } from "../../../shared/utilities/cms";
import {
  getContentValueByKey,
  getRedirectParam,
  routeCountryPath
} from "../../../shared/utilities/common";
import { getCustomerSubscriptionsByType } from "../../../shared/utilities/subscription";
import * as tagmanagerEvents from "../../../shared/utilities/tagmanagerEvents";
import amplitudeAnalytics from "../../../shared/utilities/amplitude";
import {
  amplitudeSignInMethodByLoginType,
  loginType,
} from "../../../shared/constants/login";
import OtpInput from "../Login/OtpInput";
import Welcome from "../Login/Welcome";

class CreateAccount extends Component {
  constructor(props) {
    super(props);

    this.state = {
      email: null,
      firstName: null,
      lastName: null,
      emailErrorMessage: null,
      firstNameErrorMessage: null,
      lastNameErrorMessage: null,
      phoneNumberErrorMessage: null,
      responseErrorMessage: null,
      content: [],
      subscriptionRequestCompleted: false,
      customerSubscriptions: [],
      customerSubscriptionsError: [],
      phoneNumber: null,
      continueWithOtp: false,
      otp: "",
      phoneOTPErrMessage: null,
      token: null,
      user: null,
      inviteCode: null,
      isConsentChecked: true
    };

    this.recaptchaComponentRef = React.createRef();

    this.loginGuard();
  }

  componentDidMount() {
    cms.getContent([cmsKeys.registrationPage], this);
    const { optIn, consent } = subscription.subscriptionType;
    const { EMAIL_OPT_IN } = subscription.subscriptionKeyField;
    getCustomerSubscriptionsByType([optIn, consent]).then(sub => {
      const filtereObj = (sub || []).filter(
        item =>
          (item.subscriptionTypeId === optIn &&
            item.keyField === EMAIL_OPT_IN) ||
          item.subscriptionTypeId === consent
      );
      filtereObj.sort((a, b) => {
        // Sort ascending order (0 -> 1)
        return b.isRequired - a.isRequired;
      });
      this.setState({
        customerSubscriptions: filtereObj,
        subscriptionRequestCompleted: true
      });
    });
  }

  loginGuard = () => {
    const { userToken, history } = this.props;

    if (userToken) {
      history.push(routeCountryPath("/user/profile"));
    }
  };

  handleRegister = async () => {
    if (isRecaptcha()) {
      const { setVerificationToken, setVerificationAction } = this.props;
      await setCaptchaToken(
        setVerificationToken,
        setVerificationAction,
        captchaAction.signup
      );
    }
    this.handleSubmit();
  };

  onCompleteOtp = otp => {
    this.setState({ otp });
  };

  handleResendOtp = async () => {
    const { user } = this.state;
    await userRepo.resendCustomerVerificationTelephoneOtp(
      null,
      null,
      null,
      null,
      user?.id
    );
  };

  continueWithOtp = () => {
    const { phoneNumber, otp, loading, phoneOTPErrMessage } = this.state;
    const isDisabled = !(otp.length >= 4);
    return (
      <div className="flex gap-3 flex-col w-full">
        <OtpInput
          onComplete={this.onCompleteOtp}
          length={4}
          classInput="bg-[#2C0C37] border-2 border-darkDry text-center text-4xl"
          label={
            phoneNumber?.phone
              ? `Enter the 4-digit code sent to you at ${phoneNumber.code} ${phoneNumber.phoneFormat}`
              : null
          }
          handleSubmit={this.loginWithOtp}
        />

        {phoneOTPErrMessage && (
          <p className={styles.hasError}>{phoneOTPErrMessage}</p>
        )}

        <ResendOtp handleClick={this.handleResendOtp} />

        <div className="w-full mt-4">
          <Button
            isLoading={loading}
            disabled={isDisabled}
            customContainerStyles={`${styles.buttonContainer} ${
              isDisabled ? "!bg-disabled" : ""
            } !block w-full bg-button text-white text-center py-2 h-11`}
            handleSubmit={isDisabled ? null : this.loginWithOtp}
            label="Continue"
          />
        </div>
      </div>
    );
  };

  loginWithOtp = async () => {
    const { otp, user, content } = this.state;
    const phoneSignInMethod = amplitudeSignInMethodByLoginType[loginType.phone];
    try {
      this.setState({
        loading: true
      });
      const result = await userRepo.confirmCustomerSignup(otp, user.id);
      const { data } = result;
      if (data?.confirmCustomerSignup?.customer?.telephoneVerified) {
        const verifiedCustomer = data.confirmCustomerSignup.customer;
        this.setState({
          user: verifiedCustomer,
          token: data?.confirmCustomerSignup?.token
        });
        amplitudeAnalytics.trackSignIn({
          signInMethod: phoneSignInMethod,
          isAccepted: true,
          customerId: verifiedCustomer?.id,
        });
      } else {
        amplitudeAnalytics.trackSignIn({
          signInMethod: phoneSignInMethod,
          isAccepted: false,
        });
      }
      this.setState({
        loading: false,
        continueWithOtp: false
      });
    } catch (e) {
      amplitudeAnalytics.trackSignIn({
        signInMethod: phoneSignInMethod,
        isAccepted: false,
      });
      this.setState({ loading: false });
      if (!e.message) {
        this.setState({
          phoneOTPErrMessage: "Something went wrong! Please try again.",
          loading: false
        });
        return;
      }

      const msgs = _.find(content, { key: cmsKeys.registrationPage });
      const errMsg = getKeyValue(msgs ? msgs.children : [], e.message);

      this.setState({
        phoneOTPErrMessage: errMsg || e.message,
        loading: false
      });
    }
  };

  handleUserWelcome = () => {
    const { setUserId, order, setUserToken, history, location } = this.props;
    const { token, user, password } = this.state;
    const redirectUrl = getRedirectParam(location);
    return (
      <Welcome
        order={order}
        setUserToken={setUserToken}
        setUserId={setUserId}
        token={token}
        handleLoginWithPhone={() => {}}
        user={user}
        password={password}
        isSignUp
        history={history}
        redirectUrl={redirectUrl}
      />
    );
  };

  /** Referral / promo flags for Amplitude (read when building the event, after signup returns). */
  getCreateAccountAmplitudeContext = () => {
    const { referralEnabled } = this.props;
    const { inviteCode, customerSubscriptions } = this.state;
    const referralCodeIncluded = Boolean(
      referralEnabled && String(inviteCode || "").trim() !== ""
    );
    const promoEmailOptIn = customerSubscriptions.some(
      item =>
        item.subscriptionTypeId === subscription.subscriptionType.optIn &&
        item.keyField === subscription.subscriptionKeyField.EMAIL_OPT_IN &&
        item.defaultValue
    );
    return { referralCodeIncluded, promoEmailOptIn };
  };

  /**
   * Handles form submit
   */
  handleSubmit = () => {
    const {
      email,
      firstName,
      lastName,
      phoneNumber,
      content,
      customerSubscriptions,
      inviteCode
    } = this.state;

    const code = phoneNumber?.code;
    const phone = phoneNumber?.phone;

    this.setState({
      responseErrorMessage: null,
      emailErrorMessage: null,
      firstNameErrorMessage: null,
      lastNameErrorMessage: null,
      phoneNumberErrorMessage: null
    });
    const subscriptions = customerSubscriptions.filter(
      item => item.defaultValue
    );
    const subscriptionIds = subscriptions.map(item =>
      parseInt(item.subscriptionId, 10)
    );

    // NULL VALUE LOGIC
    if (!email || email.length === 0)
      return this.setState({
        emailErrorMessage: "Please enter an email address."
      });
    if (!firstName)
      return this.setState({
        firstNameErrorMessage: "Please enter a first name"
      });

    if (!lastName)
      return this.setState({
        lastNameErrorMessage: "Please enter a last name"
      });

    if (!phoneNumber)
      return this.setState({
        phoneNumberErrorMessage: "Please enter a phone number"
      });

    // VALUE VALIDATION LOGIC
    if (email && isValid(email, "email")) {
      if (firstName && lastName && phoneNumber) {
        if (!this.validateSubscriptions()) {
          return false;
        }
        this.setState({ loading: true });

        userRepo
          .createAccountAndLoginV2(
            email,
            firstName,
            lastName,
            code,
            phone,
            null,
            false,
            subscriptionIds,
            inviteCode
          )
          .then(response => {
            const { referralCodeIncluded, promoEmailOptIn } =
              this.getCreateAccountAmplitudeContext();
            const signupCustomerV2 = response?.data?.signupCustomerV2;
            const customer = signupCustomerV2?.customer;
            const token = signupCustomerV2?.token ?? null;
            const accepted = Boolean(customer?.id);
            amplitudeAnalytics.trackCreateAccount({
              isAccepted: accepted,
              referralCodeIncluded,
              promoEmailOptIn,
            });
            if (!accepted) {
              this.setState({
                loading: false,
                responseErrorMessage:
                  "Something went wrong. Please try again."
              });
              return;
            }
            this.setState({
              continueWithOtp: true,
              loading: false,
              token,
              user: { ...customer, telephoneVerified: true }
            });
            tagmanagerEvents.signup("api");
            tagmanagerEvents.joinLoyalty({ email });
          })
          .catch(error => {
            const { referralCodeIncluded, promoEmailOptIn } =
              this.getCreateAccountAmplitudeContext();
            amplitudeAnalytics.trackCreateAccount({
              isAccepted: false,
              referralCodeIncluded,
              promoEmailOptIn,
            });
            content.map(registrationPage => {
              return this.setState({
                responseErrorMessage: getKeyValue(
                  registrationPage.children,
                  error.message
                ),
                loading: false
              });
            });
          });

        return true;
      }

      return this.setState({
        firstNameErrorMessage: "first name not matched"
      });
    }

    return this.setState({ emailErrorMessage: "Invalid email address " });
  };

  handleInput = (fieldName, value) => {
    this.setState({
      [fieldName]: value
    });
  };

  signIn = () => {
    const { history, location } = this.props;
    const redirectParam = getRedirectParam(location);
    const destination = redirectParam
      ? `${routeCountryPath("/login")}?redirectUrl=${encodeURIComponent(redirectParam)}`
      : routeCountryPath("/login");
    return history.push(destination);
  };

  getSMSConsent = () => {
    const { content } = this.state;
    const registrationPageContnet = _.find(content, {
      key: cmsKeys.registrationPage
    });

    return _.find(registrationPageContnet?.children, {
      key: cmsKeys.registrationPageRingCentralSmsConsent
    });
  };

  createAccountContainer = () => {
    const {
      emailErrorMessage,
      firstNameErrorMessage,
      lastNameErrorMessage,
      phoneNumberErrorMessage,
      email,
      firstName,
      lastName,
      loading,
      responseErrorMessage,
      subscriptionRequestCompleted,
      customerSubscriptions,
      customerSubscriptionsError,
      continueWithOtp,
      token,
      phoneNumber,
      inviteCode,
      content,
      isConsentChecked
    } = this.state;
    const isDisabled = !(
      phoneNumber &&
      firstName &&
      lastName &&
      email &&
      subscriptionRequestCompleted &&
      isConsentChecked
    );
    const { referralEnabled } = this.props;

    const cmsData = _.find(content, { key: cmsKeys.registrationPage });
    const title = getContentValueByKey(cmsData?.children, "title");
    const rewards = _.find(cmsData?.children, { key: "rewards" });

    return (
      <div
        className={`${styles.pageWrapper} text-dark dark:text-white w-[343px] mx-auto mb-10`}
      >
        {!token || continueWithOtp ? (
          <div
            className={`${styles.pageContainer} w-[343px] flex justify-center items-center`}
          >
            {!continueWithOtp ? (
              <Tabs active={LABELS.SIGN_UP_TAB}>
                <Tab
                  label={LABELS.SIGN_IN_TAB}
                  onClick={this.signIn}
                  className="text-white"
                />
                <Tab label={LABELS.SIGN_UP_TAB} className="text-white">
                  <h1 className="!font-montserratMedium !text-base !leading-5 !text-white mb-3">
                    {title}
                  </h1>
                  {rewards &&
                    rewards?.children.map((reward, index) => (
                      <div key={index} className="flex items-center mb-4">
                        <div className="mr-4 flex items-center justify-center w-6 h-6">
                          <ReactMarkdown className="w-full h-full">
                            {getContentValueByKey(reward.children, "image")}
                          </ReactMarkdown>
                        </div>
                        <h1 className="!font-montserratMedium !text-base !leading-5 !text-white">
                          {getContentValueByKey(reward.children, "title")}
                        </h1>
                      </div>
                    ))}
                  <div className="mb-3">
                    <PhoneWidget
                      onInput={this.handleInput}
                      fieldName="phoneNumber"
                      errorMessage={phoneNumberErrorMessage}
                      isRequired
                    />
                  </div>
                  <SimpleInput
                    handleInput={e => handleFormInput(e, this, "firstName")}
                    placeholder="First name *"
                    value={firstName}
                    name="firstName"
                    label="First name"
                    errorMessage={firstNameErrorMessage}
                    errorMessageId="createAccount-firstName-error"
                    isRequired
                    fieldContainerClass="border-[1px] border-darkElevationPrimary rounded-[40px]"
                    fieldClass={`!text-white w-full bg-transparent outline-none h-11 ${styles.inputLightModeOverride}`}
                    inputId="firstName"
                  />
                  <SimpleInput
                    handleInput={e => handleFormInput(e, this, "lastName")}
                    placeholder="Last name *"
                    value={lastName}
                    name="lastName"
                    label="Last name"
                    errorMessage={lastNameErrorMessage}
                    errorMessageId="createAccount-lastName-error"
                    isRequired
                    fieldContainerClass="border-[1px] border-darkElevationPrimary rounded-[40px]"
                    fieldClass={`!text-white w-full bg-transparent outline-none h-11 ${styles.inputLightModeOverride}`}
                    inputId="lastName"
                  />
                  <SimpleInput
                    handleInput={e =>
                      handleFormInput(e, this, "email", "email")}
                    placeholder="Email address *"
                    value={email}
                    label="Email address"
                    type="email"
                    errorMessage={emailErrorMessage}
                    errorMessageId="createAccount-email-error"
                    isRequired
                    fieldContainerClass="border-[1px] border-darkElevationPrimary rounded-[40px]"
                    fieldClass={`!text-white w-full bg-transparent outline-none h-11 ${styles.inputLightModeOverride}`}
                    inputId="emailAddress"
                    name="emailAddress"
                  />
                  {referralEnabled ? (
                    <SimpleInput
                      handleInput={e => handleFormInput(e, this, "inviteCode")}
                      placeholder="Referral code (optional)"
                      value={inviteCode}
                      label="Referral code (optional)"
                      fieldContainerClass="border-[1px] border-darkElevationPrimary rounded-[40px]"
                      fieldClass="!text-white w-full bg-transparent outline-none h-11 inputLightModeOverride"
                      inputId="referralCode"
                      name="referralCode"
                    />
                  ) : null}
                  {customerSubscriptions.slice(0, 1).map((item, index) => (
                    <div key={item.id} className={styles.registerCheckbox}>
                      <CheckBox
                        onClick={() => this.toggleCheckbox(index)}
                        isChecked={item.defaultValue}
                        isRequired={item.isRequired}
                        ariaDescribedBy={
                          customerSubscriptionsError[index]
                            ? `createAccount-subscription-error-${item.subscriptionId}`
                            : undefined
                        }
                        label={(
                          <div className="text-white relative">
                            <sup
                              style={{
                                position: "absolute",
                                top: "6px",
                                left: "-6px"
                              }}
                            >
                              *
                            </sup>
                            <span
                              // eslint-disable-next-line react/no-danger
                              dangerouslySetInnerHTML={{
                                __html: item.description
                              }}
                            />
                          </div>
                        )}
                      />
                      {customerSubscriptionsError[index] && (
                        <p
                          id={`createAccount-subscription-error-${item.subscriptionId}`}
                          className={styles.hasError}
                        >
                          You must agree to `
                          {item.name}
                        </p>
                      )}
                    </div>
                  ))}
                  <p
                    className={`${styles.smsConsentText} text-white flex items-center gap-2 relative`}
                  >
                    <CheckBox
                      onClick={() => this.toggleSMSConsent()}
                      isChecked={isConsentChecked}
                      isRequired
                      ariaLabelledby="createAccount-sms-consent-label"
                      ariaDescribedBy={
                        !isConsentChecked
                          ? "createAccount-sms-consent-error"
                          : undefined
                      }
                      label={null}
                    />
                    <div id="createAccount-sms-consent-label" className="relative">
                      <sup
                        style={{
                          position: "absolute",
                          top: "6px",
                          left: "-6px"
                        }}
                      >
                        *
                      </sup>
                      <ReactMarkdown>
                        {this.getSMSConsent()?.value || ""}
                      </ReactMarkdown>
                    </div>
                  </p>
                  {!isConsentChecked && (
                    <p
                      id="createAccount-sms-consent-error"
                      className={styles.hasError}
                    >
                      You must agree to the SMS consent
                    </p>
                  )}
                  {customerSubscriptions.slice(1).map((item, sliceIndex) => {
                    const subIndex = sliceIndex + 1;
                    return (
                      <div key={item.id} className={styles.registerCheckbox}>
                        <CheckBox
                          onClick={() => this.toggleCheckbox(subIndex)}
                          isChecked={item.defaultValue}
                          isRequired={item.isRequired}
                          ariaDescribedBy={
                          customerSubscriptionsError[subIndex]
                            ? `createAccount-subscription-error-${item.subscriptionId}`
                            : undefined
                        }
                          label={(
                            <div
                              className="text-white"
                            // eslint-disable-next-line react/no-danger
                              dangerouslySetInnerHTML={{
                              __html: item.description
                            }}
                            />
                        )}
                        />
                        {customerSubscriptionsError[subIndex] && (
                        <p
                          id={`createAccount-subscription-error-${item.subscriptionId}`}
                          className={styles.hasError}
                        >
                          You must agree to `
                          {item.name}
                        </p>
                      )}
                      </div>
                    );
                  })}
                  {responseErrorMessage && (
                    <p className={styles.hasError}>{responseErrorMessage}</p>
                  )}
                  <div className={styles.actionsContainer}>
                    <Button
                      isLoading={loading}
                      disabled={isDisabled}
                      handleSubmit={isDisabled ? null : this.handleRegister}
                      customContainerStyles={`
                        ${styles.buttonContainer}
                        ${isDisabled ? "!bg-disabled" : ""}
                        !block w-full bg-button text-white text-center py-2 h-11
                      `}
                      label="Create Account"
                    />
                  </div>
                  {/* SSO CONTAINER */}
                  <div className={styles.ssoContainer}>
                    <p
                      className={`${styles.privacyTermsCopy} text-darkElevationSecondary`}
                    >
                      <Link to="/privacy-policy">Privacy Policy</Link>
                      &nbsp; & &nbsp;
                      <Link to="/terms">Terms and Conditions</Link>
                    </p>
                  </div>
                </Tab>
              </Tabs>
            ) : (
              <div className="my-10">{this.continueWithOtp()}</div>
            )}
          </div>
        ) : (
          <div
            className={`${styles.pageContainer} w-[343px] flex justify-center items-center`}
          >
            {this.handleUserWelcome()}
          </div>
        )}
      </div>
    );
  };

  toggleCheckbox = index => {
    const { customerSubscriptions } = this.state;
    customerSubscriptions[index].defaultValue = !customerSubscriptions[index]
      .defaultValue;
    let newSubscriptionRequestCompleted;

    if (this.validateSubscriptions()) {
      newSubscriptionRequestCompleted = true;
    } else {
      newSubscriptionRequestCompleted = false;
    }
    this.setState({
      customerSubscriptions,
      subscriptionRequestCompleted: newSubscriptionRequestCompleted
    });
  };

  toggleSMSConsent = () => {
    const { isConsentChecked } = this.state;
    this.setState({ isConsentChecked: !isConsentChecked });
  };

  validateSubscriptions = () => {
    const { customerSubscriptions } = this.state;
    const customerSubscriptionsError = customerSubscriptions.map(
      item => !item.defaultValue && item.isRequired
    );

    this.setState({ customerSubscriptionsError });

    // Return true if no errors
    return customerSubscriptionsError.every(error => !error);
  };

  render() {
    return <this.createAccountContainer />;
  }
}

CreateAccount.propTypes = {
  setUserId: PropTypes.func.isRequired,
  setUserToken: PropTypes.func.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired
  }).isRequired,
  location: PropTypes.shape({
    search: PropTypes.string
  }),
  userToken: PropTypes.string,
  order: PropTypes.shape({
    code: PropTypes.string
  }),
  setVerificationToken: PropTypes.func.isRequired,
  setVerificationAction: PropTypes.func.isRequired,
  referralEnabled: PropTypes.bool
};

CreateAccount.defaultProps = {
  userToken: null,
  order: null,
  referralEnabled: false,
  location: null
};

const mapStateToProps = state => {
  const { userToken } = state.user;
  const { order, loyalty } = state;
  return {
    userToken,
    order,
    referralEnabled: loyalty.referralEnabled
  };
};

export const mapDispatchToProps = dispatch => ({
  setUserToken: value => dispatch(userActions.setUserToken(value)),
  setUserId: value => dispatch(userActions.setUserId(value)),
  setVerificationToken: value =>
    dispatch(elementsActions.setVerificationToken(value)),
  setVerificationAction: value =>
    dispatch(elementsActions.setVerificationAction(value))
});

export default connect(mapStateToProps, mapDispatchToProps)(CreateAccount);
